import { useCallback, useEffect, useRef, useState } from 'react'
import Map, { Layer, Popup, Source, type MapMouseEvent, type MapRef } from 'react-map-gl/mapbox'
import type { PaddingOptions } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { Alert, Text, useMantineColorScheme } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { determineMapCenter } from '@/components/Navigation/utils/mapUtils'
import { useMapResize } from '@/hooks/useMapResize'
import {
  FOOTPRINT_MIN_ZOOM,
  PAIR_LINE_COLOR,
  REJECTED_LINK_COLOR,
  SELECTED_OUTLINE_COLOR,
  type ComparisonLayers,
  type LngLatBounds,
} from './comparison'
import { DETAIL_PANEL_WIDTH } from './EntityDetail'

const INTERACTIVE_LAYERS = ['footprint-fill', 'dots', 'selected-footprint-fill', 'selected-dots']
const FIT_PADDING = 48
const FIT_MAX_ZOOM = 19

export type MapFocus = { bounds: LngLatBounds; nonce: number }

type Props = {
  layers: ComparisonLayers
  /** Overlay with only the selected entity, drawn on top with the highlighted style. */
  selection: ComparisonLayers | null
  /** Bounds of the whole dataset; the map fits them whenever `fitKey` changes. */
  datasetBounds: LngLatBounds | null
  /** Identifies the compared drive pair; parameter changes keep the camera where it is. */
  fitKey: string
  /** Bounds to zoom to on demand (e.g. a list selection); re-triggered by `nonce`. */
  focus: MapFocus | null
  onSelect: (entityId: string | null) => void
}

type Hover = { longitude: number; latitude: number; plate: string; driveId: number }

/**
 * Keeps the focused entity clear of the detail panel on the right when the map is wide
 * enough; on narrow maps a large padding would make mapbox refuse to fit at all.
 */
const focusPadding = (map: MapRef): PaddingOptions => {
  const width = map.getContainer().clientWidth
  const panel = DETAIL_PANEL_WIDTH + 2 * FIT_PADDING
  const right = width > 2 * (FIT_PADDING + panel) ? panel : FIT_PADDING
  return { top: FIT_PADDING, bottom: FIT_PADDING, left: FIT_PADDING, right }
}

export function DetectionComparisonMap({ layers, selection, datasetBounds, fitKey, focus, onSelect }: Props) {
  const { t } = useTranslation('detectionComparison')
  const { colorScheme } = useMantineColorScheme()
  const mapRef = useRef<MapRef>(null)
  const [loaded, setLoaded] = useState(false)
  const [hover, setHover] = useState<Hover | null>(null)

  // Read through a ref so the fit effect reacts to the drive pair only, not to every refetch.
  const datasetBoundsRef = useRef(datasetBounds)
  datasetBoundsRef.current = datasetBounds
  const [fittedKey, setFittedKey] = useState<string | null>(null)

  useEffect(() => {
    const bounds = datasetBoundsRef.current
    if (loaded && bounds && fittedKey !== fitKey) {
      mapRef.current?.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, duration: 500 })
      setFittedKey(fitKey)
    }
  }, [loaded, fitKey, fittedKey, datasetBounds])

  // Mapbox only tracks window resizes; the container also changes size when the navigation collapses.
  useMapResize({ mapRef: loaded ? (mapRef.current ?? undefined) : undefined, dependencies: [loaded] })

  useEffect(() => {
    const map = mapRef.current
    if (loaded && focus && map) {
      map.fitBounds(focus.bounds, { padding: focusPadding(map), maxZoom: FIT_MAX_ZOOM, duration: 500 })
    }
  }, [loaded, focus])

  const handleClick = useCallback(
    (event: MapMouseEvent) => {
      const entityId = event.features?.[0]?.properties?.entityId
      onSelect(typeof entityId === 'string' ? entityId : null)
    },
    [onSelect]
  )

  const handleMouseMove = useCallback((event: MapMouseEvent) => {
    const feature = event.features?.[0]
    if (!feature?.properties) {
      setHover(null)
      return
    }
    setHover({
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
      plate: String(feature.properties.plate ?? ''),
      driveId: Number(feature.properties.driveId),
    })
  }, [])

  if (!MAPBOX_PUBLIC_TOKEN) {
    return (
      <Alert color="yellow" variant="light" m="md">
        {t('mapUnavailable')}
      </Alert>
    )
  }

  return (
    <Map
      ref={mapRef}
      id="detectionComparisonMap"
      mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
      mapStyle={`mapbox://styles/mapbox/${colorScheme === 'dark' ? 'dark-v11' : 'streets-v12'}`}
      initialViewState={{ ...determineMapCenter(), zoom: 12 }}
      style={{ width: '100%', height: '100%' }}
      interactiveLayerIds={INTERACTIVE_LAYERS}
      cursor={hover ? 'pointer' : 'grab'}
      onLoad={() => setLoaded(true)}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHover(null)}
    >
      <Source id="rejected-links" type="geojson" data={layers.rejectedLinks}>
        <Layer
          id="rejected-links"
          type="line"
          paint={{ 'line-color': REJECTED_LINK_COLOR, 'line-width': 1, 'line-opacity': 0.5, 'line-dasharray': [4, 5] }}
        />
      </Source>
      <Source id="pair-lines" type="geojson" data={layers.pairLines}>
        <Layer
          id="pair-lines"
          type="line"
          paint={{ 'line-color': PAIR_LINE_COLOR, 'line-width': 2, 'line-opacity': 0.9 }}
        />
      </Source>
      <Source id="footprints" type="geojson" data={layers.footprints}>
        <Layer
          id="footprint-fill"
          type="fill"
          minzoom={FOOTPRINT_MIN_ZOOM}
          paint={{ 'fill-color': ['get', 'color'], 'fill-opacity': 0.75 }}
        />
        <Layer
          id="footprint-outline"
          type="line"
          minzoom={FOOTPRINT_MIN_ZOOM}
          paint={{ 'line-color': ['get', 'color'], 'line-width': 1 }}
        />
      </Source>
      <Source id="dots" type="geojson" data={layers.dots}>
        <Layer
          id="dots"
          type="circle"
          maxzoom={FOOTPRINT_MIN_ZOOM}
          paint={{
            'circle-radius': 4,
            'circle-color': ['get', 'color'],
            'circle-opacity': 0.75,
            'circle-stroke-color': ['get', 'color'],
            'circle-stroke-width': 1,
          }}
        />
      </Source>

      {selection && (
        <>
          <Source id="selected-pair-lines" type="geojson" data={selection.pairLines}>
            <Layer
              id="selected-pair-lines"
              type="line"
              paint={{ 'line-color': PAIR_LINE_COLOR, 'line-width': 3, 'line-opacity': 0.9 }}
            />
          </Source>
          <Source id="selected-footprints" type="geojson" data={selection.footprints}>
            <Layer
              id="selected-footprint-fill"
              type="fill"
              minzoom={FOOTPRINT_MIN_ZOOM}
              paint={{ 'fill-color': ['get', 'color'], 'fill-opacity': 0.95 }}
            />
            <Layer
              id="selected-footprint-outline"
              type="line"
              minzoom={FOOTPRINT_MIN_ZOOM}
              paint={{ 'line-color': SELECTED_OUTLINE_COLOR, 'line-width': 3 }}
            />
          </Source>
          <Source id="selected-dots" type="geojson" data={selection.dots}>
            <Layer
              id="selected-dots"
              type="circle"
              maxzoom={FOOTPRINT_MIN_ZOOM}
              paint={{
                'circle-radius': 7,
                'circle-color': ['get', 'color'],
                'circle-opacity': 0.95,
                'circle-stroke-color': SELECTED_OUTLINE_COLOR,
                'circle-stroke-width': 3,
              }}
            />
          </Source>
        </>
      )}

      {hover && (
        <Popup
          longitude={hover.longitude}
          latitude={hover.latitude}
          closeButton={false}
          closeOnClick={false}
          anchor="bottom"
          offset={8}
        >
          <Text size="sm">
            {hover.plate || t('list.noPlate')} · {t('display.drive', { id: hover.driveId })}
          </Text>
        </Popup>
      )}
    </Map>
  )
}
